import {getAuth} from 'firebase/auth';
import {PurchasePage} from '../purchasePage';
import {LoadingVisController} from '../../../htmlHelpers/loadingVisController';
import {Infobox} from '../../../htmlHelpers/infobox';
import {
  ISessionService,
  ProductInfoV2,
  Session,
  SessionInfo,
} from '../../../services/session';
import {ApiError} from '../../../services/api';
import {IUserService, User} from '../../../services/user';
import {ITrackingService} from '../../../services/gtmHelper';
import {TranslateLegacy} from '../../../services/translateLegacy';
import {HtmlHelper} from '../../../services/htmlHelper';
import {
  clearHtmlContent,
  getElement,
  getSelectValue,
  setHtmlContent,
} from '../../../services/html';
import {
  PURCHASE_PAGE_DONE,
  PURCHASE_PAGE_LOGIN,
  PURCHASE_PAGE_PRODSELECT,
} from '../../../config/paths';
import {HTML_PURCHASE_CHECKOUT_IDS} from '../../../const/html';
import {AuthService} from '../../../services/authService';
import {ITranslateService} from '../../../services/translateService';
import {EnvConfig} from '../../../config/envConfig.interface';
import {IPurchaseService} from '../../../services/purchase';
import {IBindingService} from '../../../services/bindingService';

export type CheckoutPageConfig = {
  adyen: boolean;
};

export class CheckoutPage extends PurchasePage {
  private readonly config: CheckoutPageConfig;
  private visController: LoadingVisController | null = null;
  private errorNode: HTMLElement | null = null;

  constructor(
    protected configuration: EnvConfig,
    protected authService: AuthService,
    protected translateService: ITranslateService,
    protected sessionService: ISessionService,
    protected userService: IUserService,
    protected trackingService: ITrackingService,
    protected purchaseService: IPurchaseService,
    protected bindingService: IBindingService,
  ) {
    super(
      configuration,
      authService,
      translateService,
      sessionService,
      userService,
      trackingService,
      purchaseService,
      bindingService,
    );
    const env = configuration;
    this.config = {
      adyen: true,
    };

    if (this.config.adyen) {
      HtmlHelper.instance.loadStyle(env.adyen.embed.css.url, {
        integrity: env.adyen.embed.css.integrity,
        crossOrigin: env.adyen.embed.css.crossOrigin,
      });
    }
  }

  public render(): void {
    if (this.config.adyen) {
      void this.renderCheckoutFormAdyen();
    }

    this.renderVariableBindings();

    if (!this.purchaseService.hasProductSelected) {
      HtmlHelper.instance.redirectTo(PURCHASE_PAGE_PRODSELECT);
      return;
    }

    getAuth().onAuthStateChanged(async user => {
      const isAnonymous = await User.instance.isAnonymous();
      if (user === null || isAnonymous) {
        // redirect to login page
        HtmlHelper.instance.redirectTo(PURCHASE_PAGE_LOGIN);
      }
    });

    // tracking event
    this.trackCheckout();
  }

  private trackCheckout() {
    // `begin_checkout` is a pre-built event for GoogleAnalytics Ecommerce
    // so we can't really extend it. Instead we create another custom one
    // to pass parameters.
    this.trackingService.trackEvent('checkout_initiated', {}, () => {
      this.trackingService.trackBeginCheckoutEvent();
    });
  }

  private clearError(): void {
    clearHtmlContent(this.errorNode);
  }

  private async setError(err: ApiError): Promise<void> {
    let msg = err.message;
    let positive = false;

    if (err.code === 'payment/unavailableCountry') {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      msg += ` ${(err as any).userCountry}`;
    }

    if (
      err.code === 'payment/unavailableAlreadySubscription' ||
      (err.code as string) === 'payment_unavailableAccountAlreadySubscription'
    ) {
      msg = await TranslateLegacy.instance.getByKey(
        'payment.errorUnavailableAlreadySubscription',
        'Congratulations! You already have a running subscription!',
      );
      positive = true;
    }

    const dropinErrMsg = this.dropinErrorToMsg(err);
    if (dropinErrMsg) msg = dropinErrMsg;

    if (positive) setHtmlContent(this.errorNode, Infobox(msg, false));
    else setHtmlContent(this.errorNode, Infobox(msg, true, err.executionId));
    this.setPaymentProcessingLoading(false);
  }

  private setErrorMessage(msg: string): void {
    setHtmlContent(this.errorNode, Infobox(msg, true));
    this.setPaymentProcessingLoading(false);
  }

  private renderVariableBindings(): void {
    const product = Session.instance.getObject<ProductInfoV2>(
      SessionInfo.purchase_productInfoV2,
    );
    if (product) {
      this.bindingService.updateSelectedProduct(product);
    }
  }

  private async renderCheckoutFormAdyen(): Promise<void> {
    this.errorNode = getElement(
      `#${HTML_PURCHASE_CHECKOUT_IDS.errorContainer}`,
    );
    if (!this.errorNode)
      throw new Error(
        'could not initialize checkout form - error node missing',
      );

    this.visController = new LoadingVisController(
      getElement('.sc-loading-visible'),
      getElement('.sc-loading-hidden'),
      this.errorNode,
    );
    this.visController.startLoading();

    void this.setupAdyen();
  }

  private async setupAdyen() {
    const dropinNode = getElement(
      `#${HTML_PURCHASE_CHECKOUT_IDS.formContainer}`,
    );
    if (!dropinNode)
      throw new Error('could not initialize checkout form - form container');

    this.visController?.startLoading();

    try {
      const dropinElem = await this.purchaseService.setupAdyen({
        dropinHtmlNode: dropinNode,

        onSubmitCallback: async ({data, actions}) => {
          this.setPaymentProcessingLoading(true);

          console.log('on submit callback', data);

          const invoiceCountryCode = getSelectValue(
            getElement(`#${HTML_PURCHASE_CHECKOUT_IDS.invoicecountry}`),
          );
          if (!invoiceCountryCode) {
            this.setErrorMessage(
              await TranslateLegacy.instance.getByKey(
                'payment.errorNoInvoiceCountry',
                'Please select an invoice country!',
              ),
            );
            return;
          }

          try {
            const result = await this.purchaseService.adyenMakePayment(
              data,
              invoiceCountryCode,
            );
            console.log('result', {...result});
            if (result.complete) {
              this.showPaymentSuccess();
            } else {
              // show form again
              this.setPaymentProcessingLoading(false);
            }
            actions.resolve({
              resultCode: result.resultCode,
              action: result.action,
            });
          } catch (err) {
            if (err instanceof Error) {
              console.log('error fetching', {...err});
              if (err instanceof ApiError && err.isApiError) {
                void this.setError(err);
              } else {
                this.setErrorMessage(err.message);
              }
              this.setPaymentProcessingLoading(false);
            }
            actions.reject();
          }
        },
        onDetailsSubmitCallback: async ({data, actions}) => {
          this.setPaymentProcessingLoading(true);
          try {
            const result = await this.purchaseService.adyenProvideDetails(data);
            console.log('detailsd result', {...result});
            if (result.complete) {
              this.showPaymentSuccess();
            } else {
              // show form again
              this.setPaymentProcessingLoading(false);
            }
            actions.resolve({
              resultCode: result.resultCode,
              action: result.action,
            });
          } catch (err) {
            if (err instanceof Error) {
              console.log('error fetching', {...err});
              if (err instanceof ApiError && err.isApiError) {
                void this.setError(err);

                // reset adyen dropin
                console.log('will reset dropin...');
                dropinElem.remove();
                void this.setupAdyen();
              } else {
                this.setErrorMessage(err.message);
              }
              this.setPaymentProcessingLoading(false);
            }
            actions.reject();
          }
        },
      });

      // show form and hide loading overlay
      this.visController?.finishLoading(true);
    } catch (err) {
      if (err instanceof Error) {
        console.log('error fetching', {...err});
        if (err instanceof ApiError && err.isApiError) {
          void this.setError(err);
        } else {
          this.setErrorMessage(err.message);
        }
        this.visController?.finishLoading(false);
      }
    }
  }

  private setPaymentProcessingLoading(isLoading: boolean): void {
    if (isLoading) {
      getElement(
        `#${HTML_PURCHASE_CHECKOUT_IDS.processingContainer}`,
      )?.classList.add('sc-visible');
      this.clearError();
    } else {
      getElement(
        `#${HTML_PURCHASE_CHECKOUT_IDS.processingContainer}`,
      )?.classList.remove('sc-visible');
    }

    // TODO: show/hide spinner
  }

  private showPaymentSuccess() {
    HtmlHelper.instance.redirectTo(PURCHASE_PAGE_DONE);
  }
}
